/**
 * Datadog RUM Configuration for AWS IIC App
 * 
 * This module initializes Datadog Real User Monitoring (RUM) to track:
 * - Page views and user sessions
 * - CLI login attempts (nvsec aws auth)
 * - Authentication success/failure
 * - Performance metrics
 * 
 * Credentials are injected at build time via REACT_APP_* environment variables.
 */

import { datadogRum } from '@datadog/browser-rum';

// Check if Datadog is already initialized
let isInitialized = false;

/**
 * Initialize Datadog RUM
 * Called once at app startup
 */
export function initDatadogRum(): void {
  // Prevent double initialization
  if (isInitialized) {
    console.log('[Datadog] Already initialized, skipping');
    return;
  }

  // Get credentials from build-time environment variables
  const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

  // Skip if credentials not configured
  if (!applicationId || !clientToken) {
    console.log('[Datadog] RUM credentials not configured, skipping initialization');
    console.log('[Datadog] To enable, set REACT_APP_DATADOG_APPLICATION_ID and REACT_APP_DATADOG_CLIENT_TOKEN');
    return;
  }

  try {
    datadogRum.init({
      applicationId: applicationId,
      clientToken: clientToken,
      site: 'datadoghq.com',
      service: 'aws-iic-app',
      env: process.env.NODE_ENV || 'production',
      version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [/https:\/\/.*\.nvidia\.com/],
    });

    isInitialized = true;
    console.log('[Datadog] RUM initialized successfully');
  } catch (error) {
    console.error('[Datadog] Failed to initialize RUM:', error);
  }
}

/**
 * Track custom action/event
 */
export function trackAction(name: string, context?: Record<string, unknown>): void {
  if (!isInitialized) return;
  
  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error('[Datadog] Failed to track action:', error);
  }
}

/**
 * Track CLI login initiated
 */
export function trackCliLoginInitiated(client: string = 'nvsec'): void {
  trackAction('cli.login.initiated', { client, source: 'cli' });
}

/**
 * Track CLI authentication success
 */
export function trackCliAuthSuccess(accountCount: number, roleCount: number): void {
  trackAction('cli.auth.success', { accountCount, roleCount, source: 'cli' });
}

/**
 * Track CLI authentication failure
 */
export function trackCliAuthFailed(error: string): void {
  trackAction('cli.auth.failed', { error, source: 'cli' });
}

/**
 * Track CLI callback sent
 */
export function trackCliCallbackSent(success: boolean, accountCount: number): void {
  trackAction('cli.callback.sent', { success, accountCount, source: 'cli' });
}

/**
 * Track AWS account fetch
 */
export function trackAccountFetch(accountCount: number, success: boolean): void {
  trackAction('accounts.fetch', { accountCount, success });
}

/**
 * Track AWS Console open
 */
export function trackConsoleOpen(accountId: string, mpaName: string): void {
  trackAction('console.open', { accountId, mpaName });
}

/**
 * Set user info for session tracking
 */
export function setUser(email: string, userId?: string): void {
  if (!isInitialized) return;
  
  try {
    datadogRum.setUser({
      id: userId || email,
      email: email,
      // Only track domain for privacy
      name: email.split('@')[0],
    });
  } catch (error) {
    console.error('[Datadog] Failed to set user:', error);
  }
}

export default {
  initDatadogRum,
  trackAction,
  trackCliLoginInitiated,
  trackCliAuthSuccess,
  trackCliAuthFailed,
  trackCliCallbackSent,
  trackAccountFetch,
  trackConsoleOpen,
  setUser,
};

